import { ChevronDown, Tag, X, Loader2 } from "lucide-react";
import { useState } from "react";
import type { Checkout, ShippingRate } from "@melhot/shared";
import { formatMoney } from "@melhot/shared/money";
import { api } from "../api";

export function ProductLine({ title, quantity, amount, imageUrl }: { title: string; quantity: number; amount: number; imageUrl?: string }) {
  return (
    <div className="grid grid-cols-[54px_minmax(0,1fr)_auto] gap-3 py-3">
      <img src={imageUrl} alt="" className="h-14 w-14 shrink-0 border border-slate-200 bg-soft object-cover" />
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-medium leading-5">{title} x {quantity}</p>
      </div>
      <p className="shrink-0 text-right text-sm font-semibold">{formatMoney(amount)}</p>
    </div>
  );
}

export function CouponInput({ checkoutId, couponCode, onUpdate }: { checkoutId?: string; couponCode?: string; onUpdate?: (updated: Checkout) => void }) {
  const [open, setOpen] = useState(false);
  const [inputCode, setInputCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkoutId || !inputCode.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const updated = await api.applyCoupon(checkoutId, inputCode.trim());
      if (onUpdate) onUpdate(updated);
      setInputCode("");
      setOpen(false);
    } catch (err: any) {
      setError(err.message || "Cupom inválido ou expirado.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!checkoutId) return;
    setLoading(true);
    setError(null);
    try {
      const updated = await api.removeCoupon(checkoutId);
      if (onUpdate) onUpdate(updated);
    } catch (err: any) {
      setError(err.message || "Erro ao remover cupom.");
    } finally {
      setLoading(false);
    }
  };

  if (couponCode) {
    return (
      <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-800">
        <div className="flex items-center gap-2 font-bold uppercase">
          <Tag size={14} className="text-emerald-600" />
          <span>Cupom {couponCode} aplicado</span>
        </div>
        <button
          type="button"
          onClick={handleRemove}
          disabled={loading}
          className="flex items-center gap-1 text-slate-500 hover:text-red-600 font-semibold"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <X size={14} />} Remover
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full border border-slate-200 bg-white px-4 py-3 text-center text-sm font-semibold underline underline-offset-2 hover:bg-slate-50"
      >
        Adicionar cupom de desconto
      </button>
    );
  }

  return (
    <form onSubmit={handleApply} className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={inputCode}
          onChange={(e) => setInputCode(e.target.value.toUpperCase())}
          placeholder="CÓDIGO DO CUPOM"
          className="field flex-1 text-xs uppercase tracking-wider font-semibold"
          autoFocus
        />
        <button
          type="submit"
          disabled={loading || !inputCode.trim()}
          className="bg-ink px-4 py-2 text-xs font-bold text-white rounded hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loading && <Loader2 size={12} className="animate-spin" />} Aplicar
        </button>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </form>
  );
}

export function OrderSummary({ checkout, rates, onCheckoutUpdate }: { checkout?: Checkout; rates?: ShippingRate[]; onCheckoutUpdate?: (updated: Checkout) => void }) {
  const [expanded, setExpanded] = useState(false);
  const selected = rates?.find((rate) => rate.id === checkout?.selectedShippingMethod);
  const itemCount = checkout?.items.reduce((total, item) => total + item.quantity, 0) ?? 0;

  return (
    <section className="border-b border-slate-200 bg-white lg:border-b-0">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left lg:pointer-events-none lg:cursor-default lg:px-0 lg:pb-4 lg:pt-0"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
      >
        <span className="flex min-w-0 items-center gap-2 text-sm">
          <ChevronDown size={15} className={`shrink-0 text-accent transition lg:hidden ${expanded ? "rotate-180" : ""}`} />
          <span className="font-medium underline underline-offset-2 lg:no-underline">{expanded ? "Ocultar detalhes do pedido" : "Ver detalhes do pedido"}</span>
          <span className="text-xs text-accent lg:hidden">({itemCount})</span>
        </span>
        <strong className="text-lg font-semibold lg:text-xl">{formatMoney(checkout?.total ?? 0)}</strong>
      </button>

      <div className={`${expanded ? "block" : "hidden"} px-4 pb-4 lg:block lg:px-0 lg:pb-0`}>
        <div className="max-h-[280px] overflow-y-auto border-t border-slate-200 pt-2 pr-1 lg:max-h-none lg:overflow-visible lg:border-t-0 lg:pt-0">
          {checkout?.items.map((item) => <ProductLine key={item.id} title={item.title} quantity={item.quantity} amount={item.finalUnitPrice * item.quantity} imageUrl={item.imageUrl} />)}
        </div>
        <div className="mt-3">
          <CouponInput checkoutId={checkout?.publicId} couponCode={checkout?.couponCode} onUpdate={onCheckoutUpdate} />
        </div>
        <div className="mt-4 space-y-2 border-t border-slate-200 pt-4 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><strong>{formatMoney(checkout?.subtotal ?? 0)}</strong></div>
          <div className="flex justify-between text-emerald-600 font-medium"><span>Desconto (PIX + Cupom)</span><strong>-{formatMoney(checkout?.discountAmount ?? 0)}</strong></div>
          <div className="flex justify-between"><span>Frete</span><strong>{selected ? formatMoney(selected.amount) : formatMoney(checkout?.shippingAmount ?? 0)}</strong></div>
          <div className="flex justify-between pt-2 text-xl text-ink"><span>Total</span><strong>{formatMoney(checkout?.total ?? 0)}</strong></div>
        </div>
      </div>
    </section>
  );
}
